import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { COLOR_SUCCESS, COLOR_ERROR } from '../../utils/colors';
import {
  Card,
  Table,
  Button,
  Typography,
  Spin,
  message,
  Dropdown,
  Row,
  Col,
  Statistic,
  Select,
} from 'antd';
import {
  FileTextOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import apiService from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const CURRENT_YEAR = dayjs().year();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const RevenueReport = () => {
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);

  useEffect(() => {
    fetchRevenueData(selectedYear);
  }, [selectedYear]);

  const fetchRevenueData = async (year) => {
    try {
      setLoading(true);
      const response = await apiService.reports.getRevenueReport({ format: 'json', year });
      setRevenueData(response.data.data || []);
    } catch (err) {
      message.error('Failed to fetch revenue data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setLoading(true);
      const response = await apiService.reports.getRevenueReport({ format, year: selectedYear });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `revenue_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(`Report exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting report:', error);
      message.error('Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  const exportMenuItems = [
    {
      key: 'csv',
      icon: <FileTextOutlined />,
      label: 'Export as CSV',
      onClick: () => handleExport('csv')
    },
    {
      key: 'excel',
      icon: <FileTextOutlined />,
      label: 'Export as Excel',
      onClick: () => handleExport('excel')
    }
  ];

  const columns = [
    {
      title: 'Month',
      dataIndex: 'monthLabel',
      key: 'monthLabel',
      width: 150,
    },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (value) => `£${Number(value).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
    {
      title: 'Paid Revenue',
      dataIndex: 'paidRevenue',
      key: 'paidRevenue',
      render: (value) => `£${Number(value).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
    {
      title: 'Unpaid Revenue',
      dataIndex: 'unpaidRevenue',
      key: 'unpaidRevenue',
      render: (value) => `£${Number(value).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
    {
      title: 'Total Expense',
      dataIndex: 'totalExpense',
      key: 'totalExpense',
      render: (value) => `£${Number(value || 0).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
    {
      title: 'Unpaid Expense',
      dataIndex: 'unpaidExpense',
      key: 'unpaidExpense',
      render: (value) => `£${Number(value || 0).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
  ];

  // Summary stats from monthly data
  const monthCount = revenueData.length || 1;
  const totalRevenue = revenueData.reduce((sum, m) => sum + (m.totalRevenue || 0), 0);
  const totalExpense = revenueData.reduce((sum, m) => sum + (m.totalExpense || 0), 0);
  const totalNetIncome = totalRevenue - totalExpense;
  const avgRevenuePerMonth = totalRevenue / monthCount;
  const avgExpensePerMonth = totalExpense / monthCount;
  const avgNetIncomePerMonth = totalNetIncome / monthCount;
  const profitMargin = totalRevenue > 0 ? (totalNetIncome / totalRevenue) * 100 : 0;

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>Revenue Report</Title>
          <Text type="secondary">Monthly revenue breakdown with paid and unpaid amounts</Text>
        </div>
        <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 100 }}>
          {YEAR_OPTIONS.map(y => <Option key={y} value={y}>{y}</Option>)}
        </Select>
      </div>



      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Avg Revenue / Month"
              value={avgRevenuePerMonth}
              precision={2}
              prefix="£"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Avg Expense / Month"
              value={avgExpensePerMonth}
              precision={2}
              prefix="£"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Avg Net Income / Month"
              value={avgNetIncomePerMonth}
              precision={2}
              prefix="£"
              valueStyle={{ color: avgNetIncomePerMonth >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Profit Margin"
              value={profitMargin}
              precision={1}
              suffix="%"
              valueStyle={{ color: profitMargin >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={`Monthly Revenue Breakdown — ${selectedYear}`}
        extra={
          <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button type="primary" icon={<DownloadOutlined />}>
              Export
            </Button>
          </Dropdown>
        }
      >
        <Table
          columns={columns}
          dataSource={revenueData}
          rowKey="month"
          pagination={{ pageSize: 12 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Card title="Revenue Chart" style={{ marginTop: '24px' }}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={revenueData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="monthLabel" angle={-45} textAnchor="end" height={100} />
            <YAxis />
            <Tooltip formatter={(value) => `£${Number(value).toFixed(2)}`} contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 600 }} cursor={false} />
            <Legend />
            <Bar dataKey="totalRevenue" fill="#3f8600" name="Total Revenue" />
            <Bar dataKey="paidRevenue" fill={COLOR_SUCCESS} name="Paid Revenue" />
            <Bar dataKey="unpaidRevenue" fill={COLOR_ERROR} name="Unpaid Revenue" />
            <Bar dataKey="totalExpense" fill="#cf1322" name="Total Expense" />
            <Bar dataKey="unpaidExpense" fill="#ff7875" name="Unpaid Expense" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};

export default RevenueReport;
